import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { PRICING_TIERS } from "@/lib/site";
import { Reveal } from "@/components/motion-primitives";
import { SectionHead, CtaBand, usePageMeta } from "@/components/shared";
import { ArrowRight } from "lucide-react";

export default function Pricing() {
  usePageMeta("Pricing", "Transparent Bermuda vacation rental management pricing — co-hosting from 15%, full-service from 25%, white-glove from 50% of accommodation revenue.");
  return (
    <main>
      <section className="bg-sand-light">
        <div className="mx-auto max-w-[1440px] px-6 pb-20 pt-40 lg:px-10">
          <SectionHead
            overline="Transparent Pricing"
            title={<>Premium service.<br /><span className="italic text-bermuda-dark">Honest numbers.</span></>}
            copy="No fake discounts, no pricing tricks, no hidden percentages. Three clear plans — and a written, custom proposal before you commit to anything."
            align="center"
          />
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-6 pb-24 lg:px-10">
        <div className="grid gap-8 lg:grid-cols-3">
          {PRICING_TIERS.map((tier, i) => (
            <Reveal key={tier.name} delay={i * 0.1} className="h-full">
              <div
                data-testid={`pricing-tier-${tier.name.toLowerCase().replace(/\s/g, "-")}`}
                className={`relative flex h-full flex-col p-9 lg:p-11 ${
                  tier.featured
                    ? "bg-navy text-ivory shadow-2xl shadow-navy/25 lg:-my-6 lg:py-16"
                    : "border border-navy/10 bg-white"
                }`}
              >
                {tier.featured && (
                  <span className="absolute right-6 top-6 bg-bermuda px-3 py-1 text-[9px] font-bold uppercase tracking-[0.2em] text-navy">
                    Most Chosen
                  </span>
                )}
                <h3 className={`font-serif text-2xl ${tier.featured ? "text-ivory" : "text-navy"}`}>{tier.name}</h3>
                <div className="mt-6 flex items-baseline gap-2">
                  <span className={`text-[10px] uppercase tracking-[0.2em] ${tier.featured ? "text-ivory/60" : "text-navy/50"}`}>Starting at</span>
                </div>
                <div className="mt-1 flex items-baseline gap-3">
                  <span className={`font-serif text-6xl font-light ${tier.featured ? "text-bermuda" : "text-navy"}`}>{tier.rate}</span>
                  <span className={`text-xs ${tier.featured ? "text-ivory/60" : "text-navy/50"}`}>{tier.tagline}</span>
                </div>
                <p className={`mt-5 text-[13px] leading-relaxed ${tier.featured ? "text-ivory/70" : "text-navy/60"}`}>{tier.best}</p>
                <ul className="mt-7 flex-1 space-y-3">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-[13px]">
                      <Check size={14} strokeWidth={2.5} className={`mt-0.5 shrink-0 ${tier.featured ? "text-bermuda" : "text-ocean"}`} />
                      <span className={tier.featured ? "text-ivory/85" : "text-navy/75"}>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/assessment"
                  data-testid={`pricing-cta-${tier.rate.replace("%", "")}`}
                  className={`mt-9 block px-6 py-4 text-center text-[11px] font-semibold uppercase tracking-[0.18em] transition-colors duration-300 ${
                    tier.featured
                      ? "bg-bermuda text-navy hover:bg-ivory"
                      : "border border-navy/25 text-navy hover:bg-navy hover:text-ivory"
                  }`}
                >
                  {tier.cta}
                </Link>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <div className="mt-20 border-y border-navy/10 py-14 lg:py-16" data-testid="white-glove-value">
            <div className="grid gap-12 lg:grid-cols-12">
              <div className="lg:col-span-5">
                <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-bermuda-dark">Why 50%?</p>
                <h3 className="mt-4 font-serif text-3xl font-light leading-tight text-navy lg:text-4xl">
                  What White-Glove owners<br /><span className="italic text-bermuda-dark">are actually buying.</span>
                </h3>
                <p className="mt-5 max-w-md text-sm leading-relaxed text-navy/60">
                  Half the revenue sounds like a lot — until you price what it replaces: your evenings, your inbox,
                  the search for reliable cleaners and every late-night guest message. White-Glove owners are not
                  buying a service. They are buying back their time, with a local team treating the home like their own.
                </p>
              </div>
              <div className="lg:col-span-7">
                <ol className="grid gap-x-10 gap-y-9 sm:grid-cols-2">
                  {[
                    ["01", "Truly hands-off income", "You keep 50% and do nothing. No guest messages, no cleaner calls, no coordination — just clear owner reporting and a property that is always guest-ready."],
                    ["02", "A team on-island, around the clock", "Priority 24/7 support means issues are resolved before they ever reach a review. Your guests are cared for in person, by locals."],
                    ["03", "A stronger number to share", "Professional pricing, faster responses and an elevated guest experience are designed to lift occupancy and nightly rates — so your share of a stronger performing property can rival going it alone."],
                    ["04", "Concierge-level care", "Enhanced inspection schedules, detailed owner reporting and guest-experience coordination — the standard we would want for our own homes."],
                  ].map(([num, title, body]) => (
                    <li key={num} data-testid={`white-glove-value-${num}`}>
                      <span className="font-mono text-xs text-bermuda-dark">{num}</span>
                      <h4 className="mt-2 font-serif text-lg text-navy">{title}</h4>
                      <p className="mt-2 text-[13px] leading-relaxed text-navy/60">{body}</p>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="noise-overlay mt-16 grid items-center gap-8 bg-navy p-10 text-ivory lg:grid-cols-12 lg:p-14" data-testid="market-manage-bundle">
            <div className="lg:col-span-7">
              <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-bermuda">The Complete Presence</p>
              <h3 className="mt-4 font-serif text-3xl font-light leading-tight lg:text-4xl">Market + Manage.</h3>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-ivory/70">
                White-Glove property management bundled with your own direct-booking website and managed social
                media — one team running your property, your marketing and your guest experience as a single,
                coherent brand.
              </p>
              <ul className="mt-6 grid gap-2.5 text-[13px] text-ivory/85 sm:grid-cols-2">
                {["Everything in White-Glove Management", "Direct-booking website, designed & maintained", "Monthly social media management", "One point of contact for everything"].map((f) => (
                  <li key={f} className="flex items-start gap-2.5">
                    <Check size={14} strokeWidth={2.5} className="mt-0.5 shrink-0 text-bermuda" /> {f}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-5 lg:text-right">
              <p className="text-[11px] uppercase tracking-[0.2em] text-ivory/50">Bundle pricing</p>
              <p className="mt-2 font-serif text-3xl font-light text-ivory">Custom proposal</p>
              <p className="mt-2 text-xs leading-relaxed text-ivory/50">Bundled rates reflect real savings over booking each service separately.</p>
              <Link
                to="/consultation"
                data-testid="bundle-consultation-btn"
                className="group mt-6 inline-flex items-center gap-2.5 bg-bermuda px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-navy transition-colors hover:bg-ivory"
              >
                Discuss the Bundle <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="mx-auto mt-20 max-w-3xl border-l-2 border-bermuda bg-white p-8 lg:p-10">
            <h3 className="font-serif text-2xl text-navy">What is charged separately — stated plainly.</h3>
            <ul className="mt-5 space-y-3 text-sm leading-relaxed text-navy/70">
              <li className="flex gap-3"><Check size={15} className="mt-1 shrink-0 text-bermuda-dark" /> Cleaning is charged separately and quoted per property. Most owners offset it with a guest cleaning fee on their listing.</li>
              <li className="flex gap-3"><Check size={15} className="mt-1 shrink-0 text-bermuda-dark" /> Maintenance contractors and materials are charged separately, at cost, with your authorization for significant work.</li>
              <li className="flex gap-3"><Check size={15} className="mt-1 shrink-0 text-bermuda-dark" /> Custom quotes are provided for large villas, luxury homes, multiple units and unique properties.</li>
            </ul>
          </div>
        </Reveal>
      </section>

      <CtaBand title="The right plan depends on your property." copy="A property assessment gives you an exact, written proposal — revenue expectations included." />
    </main>
  );
}
